import { VesselData } from "../vessel-reg/vessel-data.model";
import { FishTicket } from "./delivery-fish-ticket.model";
import { FishingEvent } from "./fishing-event.model";
import { LookupItem } from "./lookup-item.model";
import { DeliveryStatus } from "./trip.model";

/**
 * Delivery model for groundfish
 * All properties are specific to groundfish
 */
export class GroundfishDelivery {

    status: DeliveryStatus = DeliveryStatus.New;

    //Links to operatorUserID in OperatorUser model
    skipperUserID: string = '';

    //From registration, or if joint then section from registration
    operatorName: string = '';

    //For vessel name and number - Default to last entered
    vessel: VesselData = new VesselData('', '');

    //Unique identifier for each delivery
    deliveryID: string = '';
  
    //For HMS this is the arrival date, return date for Groundfish
    returnDate!: Date;
  
    //As above, arrival for HMS, return for Groundfish
    returnPort: LookupItem = new LookupItem('', 0);
  
    //List of associated Fishing Events/Sets, slightly different between HMS and Groundfish
    sets: FishingEvent[] = [];
  
    //Once received, this delivery will get a logbook id and received date, this received date is the original submission date
    logbookId!: string;
    receivedDate!: Date;
  
    //Logbook version starts at 1, Incremented upon each submission of edits
    //submissionDate is the date of the most recently submitted edits
    logbookVersion: number = 1;
    submissionDate!: Date;  

    //All below default to last entered
    isEFP: boolean = false;

    isEMTrip: boolean = false;

    isObservedTrip: boolean = false;

    permitType: LookupItem = new LookupItem('');

    permitNumber: string = '';

    //Mimics the PSMFC schema, multiple deliveries can be linked to a landing, each delivery is basically just a fish ticket
    tickets: FishTicket[] = [];

    //Pending Delivery use only, true if a new delivery is started and fields should be filled to default values
    isNewDelivery: boolean = true;

    //For PSMFC server, set on submit - Supposed to be a GUID, using https://www.npmjs.com/package/guid-typescript
    localLandingGUID!: string;



    /**
     * On every update from the delivery detail page, this method is called to update {@link DeliveryDetailComponent.landing}
     * @param form Landing data
     */
    updateValue(form: any) {
        this.vessel = form.vessel;
        this.operatorName = form.operator;
        this.returnDate = form.deliverDate; //Not sure about this
        this.returnPort = form.landingPort;
        this.permitType = form.permitType;
        //this.permitNumber = form.permitNum;
        this.isEFP = form.isExemptedFP;
        this.isEMTrip = form.isEMTrip;
        this.isObservedTrip = form.isObservedTrip;
    }


    setValues(delivery: any) {
        let d = delivery as GroundfishDelivery;
        this.skipperUserID = d.skipperUserID;
        this.operatorName = d.operatorName;
        this.vessel = new VesselData(d.vessel.vesselName, d.vessel.vesselCoastNumber);
        this.deliveryID = d.deliveryID;     
        this.isEFP = d.isEFP;
        this.isEMTrip = d.isEMTrip;
        this.isObservedTrip = d.isObservedTrip;
        this.permitType = LookupItem.transformAnonymous(d.permitType);
        this.permitNumber = d.permitNumber;
        this.returnDate = d.returnDate ? new Date(d.returnDate) : this.returnDate;
        this.returnPort = LookupItem.transformAnonymous(d.returnPort);
        this.sets = d.sets.map(set => FishingEvent.transformAnonymous(set));
        this.tickets = d.tickets;
        this.isNewDelivery = d.isNewDelivery;
        this.logbookId = d.logbookId;
        this.receivedDate = new Date(d.receivedDate);
        this.localLandingGUID = d.localLandingGUID;
        this.logbookVersion = d.logbookVersion ?? 1;
        this.submissionDate = d.submissionDate;
    }


    static transformAnonymous(anon: any): GroundfishDelivery {
        let gf = new GroundfishDelivery();
        gf.status = anon.status ?? DeliveryStatus.New;
        gf.skipperUserID = anon.skipperUserID;
        gf.operatorName = anon.operatorName;
        gf.vessel = new VesselData(anon.vessel.vesselName, anon.vessel.vesselCoastNumber);
        gf.deliveryID = anon.deliveryID;
        if (anon.returnDate instanceof Date && isNaN(anon.returnDate)) anon.returnDate = undefined;
        gf.returnDate = anon.returnDate ? new Date(anon.returnDate) : anon.returnDate;
        gf.returnPort = LookupItem.transformAnonymous(anon.returnPort);
        gf.sets = (anon.sets as any[]).map(set => FishingEvent.transformAnonymous(set));
        gf.logbookId = anon.logbookId;
        gf.receivedDate = anon.receivedDate;
        gf.logbookVersion = anon.logbookVersion;
        gf.submissionDate = anon.submissionDate;  
        gf.isEFP = anon.isEFP;
        gf.isEMTrip = anon.isEMTrip;
        gf.isObservedTrip = anon.isObservedTrip;
        gf.permitType = LookupItem.transformAnonymous(anon.permitType);
        gf.permitNumber = anon.permitNumber;
        gf.tickets = anon.tickets;
        gf.isNewDelivery = anon.isNewDelivery;
        gf.localLandingGUID = anon.localLandingGUID;
        return gf;
    }
}
